import React, { FC } from 'react';
import {
  FieldPath,
  get,
  UseFormClearErrors,
  UseFormGetValues,
  UseFormSetError,
  UseFormSetValue,
} from 'react-hook-form';
import { FieldErrors } from 'react-hook-form';

import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import { FormSchema } from '../event-form.validation';
import { EventDocumentsFieldPath, EventDocumentsFile } from '../hooks/use-event-documents-bucket';
import { EventDocumentsAccordion } from './event-documents-accordion';
import { EventDocumentsBucket } from './event-documents-bucket';

export type EventDocumentsStakeholderBucket = {
  field: EventDocumentsFieldPath;
  /** The stakeholder's other rows — kept identical to `field` on every write of the bucket. */
  mirrorFields: EventDocumentsFieldPath[];
  /** Accordion label — the stakeholder this row issues to. */
  title: string;
};

/**
 * AC: a stakeholder with several issuance rows in the same event is still listed once, and a file
 * added to that single bucket attaches to every one of their rows. The bucket reads and writes the
 * first row's files field and mirrors each write to the rest. Shared by Share Issuance and Company
 * Incorporation so the two groupings cannot drift.
 *
 * Rows without a stakeholder yet (a half-filled form) each keep their own bucket — there is no id
 * to group them by, and merging them would attach one row's files to another.
 */
export const buildStakeholderDocumentBuckets = (
  rows: readonly ({ stakeholder?: { fullName?: string; id?: string } } | undefined)[],
  toField: (index: number) => EventDocumentsFieldPath,
  fallbackTitle: string,
): EventDocumentsStakeholderBucket[] => {
  const bucketByStakeholderId = new Map<string, EventDocumentsStakeholderBucket>();
  const buckets: EventDocumentsStakeholderBucket[] = [];

  rows.forEach((row, index) => {
    const stakeholderId = row?.stakeholder?.id;

    const existingBucket = stakeholderId && bucketByStakeholderId.get(stakeholderId);
    if (existingBucket) {
      existingBucket.mirrorFields.push(toField(index));
      return;
    }

    const bucket: EventDocumentsStakeholderBucket = {
      field: toField(index),
      mirrorFields: [],
      title: row?.stakeholder?.fullName || fallbackTitle,
    };
    if (stakeholderId) bucketByStakeholderId.set(stakeholderId, bucket);
    buckets.push(bucket);
  });

  return buckets;
};

export type EventDocumentsSectionProps = {
  clearErrors: UseFormClearErrors<FormSchema>;
  errors: FieldErrors<FormSchema>;
  formData: UseFormGetValues<FormSchema>;
  /** The event-level bucket, shown first and labelled "General". */
  generalField: EventDocumentsFieldPath;
  setError: UseFormSetError<FormSchema>;
  setFormData: UseFormSetValue<FormSchema>;
  stakeholderBuckets: EventDocumentsStakeholderBucket[];
};

/**
 * The Documents step for the event types that attach files per stakeholder as well as per event:
 * a General bucket followed by one bucket per issuance row. Shared by Share Issuance and Company
 * Incorporation so the two cannot drift.
 */
export const EventDocumentsSection: FC<EventDocumentsSectionProps> = ({
  clearErrors,
  errors,
  formData,
  generalField,
  setError,
  setFormData,
  stakeholderBuckets,
}) => {
  const errorMessageFor = (field: EventDocumentsFieldPath) => {
    // Errors are set on the bucket's own field path, so read them from the same place rather than
    // threading a message down per call site.
    const error = get(errors, field as FieldPath<FormSchema>);
    return typeof error?.message === 'string' ? error.message : undefined;
  };

  // Read at render like the bucket itself does: both callers `useWatch` the form, so this
  // re-renders on every upload, and a collapsed bucket has no mounted child to ask.
  const uploadedCountFor = (field: EventDocumentsFieldPath) =>
    ((formData(field as FieldPath<FormSchema>) as EventDocumentsFile[] | undefined) ?? []).filter(
      ({ docLink }) => Boolean(docLink),
    ).length;

  const bucketProps = { clearErrors, formData, setError, setFormData };

  return (
    <div className="flex w-full flex-col gap-4">
      <span className="text-xs font-[450] text-gray-500">
        <AppFormattedMessage id="DOCUMENTS" />
      </span>

      <EventDocumentsAccordion fileCount={uploadedCountFor(generalField)} title="General">
        <EventDocumentsBucket
          {...bucketProps}
          errorMessage={errorMessageFor(generalField)}
          field={generalField}
        />
      </EventDocumentsAccordion>

      {stakeholderBuckets.map(({ field, mirrorFields, title }) => (
        <EventDocumentsAccordion fileCount={uploadedCountFor(field)} key={field} title={title}>
          <EventDocumentsBucket
            {...bucketProps}
            errorMessage={errorMessageFor(field)}
            field={field}
            mirrorFields={mirrorFields}
          />
        </EventDocumentsAccordion>
      ))}
    </div>
  );
};
